import React, { ComponentProps } from 'react'
import { useTranslation } from 'hooks/useTranslation'
import {
  FeedBetterRoot,
  HeaderBox,
  Header,
  RecipesBox,
  RecipeIcons,
  RecipesColumn,
  BodyBox,
  ImageColumn,
  Title,
  Description,
  IconWrapper,
  ImageWrapper,
  SeeMoreButton,
  IconImage,
} from './FeedBetter.styles'
import Image, { StaticImageData } from 'next/image'
import freezeDried from '../../../../../../public/assets/home/freeze-dried-ingredient-preparation.webp'
import frozenMeat from '../../../../../../public/assets/product-v2/frozen-meat.webp'
import beef from '../../../../../../public/assets/icons/brand-refresh/beef.webp'
import lamb from '../../../../../../public/assets/icons/brand-refresh/lamb.webp'
import turkey from '../../../../../../public/assets/icons/brand-refresh/turkey.webp'
import chicken from '../../../../../../public/assets/icons/brand-refresh/chicken.webp'
import duck from '../../../../../../public/assets/icons/brand-refresh/duck.webp'
import { TranslationKeys } from 'locales/translationKeys'
import Link from 'next/link'
import { Text } from 'components/Text'

type FeedBetterOwnProps = ComponentProps<typeof FeedBetterRoot>
export type FeedBetterProps = FeedBetterOwnProps & {
  showFrozen?: boolean
  showFreezeDried?: boolean
}

export const FeedBetter = (props: FeedBetterProps) => {
  const { t } = useTranslation()
  const { showFrozen = true, showFreezeDried = true } = props
  type protein = {
    icon: StaticImageData
    alt: TranslationKeys
    link: string
  }

  const frozenProteins: protein[] = [
    {
      icon: beef,
      alt: 'recipes-overview:other-products.frozen.selection.beef',
      link: '/recipes/beef',
    },
    {
      icon: turkey,
      alt: 'recipes-overview:other-products.frozen.selection.turkey',
      link: '/recipes/turkey',
    },
    {
      icon: chicken,
      alt: 'recipes-overview:other-products.frozen.selection.chicken',
      link: '/recipes/chicken',
    },
    {
      icon: duck,
      alt: 'recipes-overview:other-products.frozen.selection.duck',
      link: '/recipes/duck',
    },
    {
      icon: lamb,
      alt: 'recipes-overview:other-products.frozen.selection.lamb',
      link: '/recipes/lamb',
    },
  ]

  const freezeDriedProteins: protein[] = [
    {
      icon: beef,
      alt: 'recipes-overview:other-products.freeze-dried.selection.beef',
      link: '/recipes/freeze-dried-raw-beef',
    },
    {
      icon: chicken,
      alt: 'recipes-overview:other-products.freeze-dried.selection.chicken',
      link: '/recipes/freeze-dried-raw-chicken',
    },
  ]

  type IconProps = {
    src: StaticImageData
    text: TranslationKeys
    link: string
  }

  const Icon = ({ src, text, link }: IconProps) => (
    <Link passHref href={link}>
      <IconWrapper>
        <IconImage>
          <Image src={src} alt={t(text)} />
        </IconImage>
        <Text size="h5" as="p" transform="capitalize" font="label">
          {t(text)}
        </Text>
      </IconWrapper>
    </Link>
  )

  return (
    <FeedBetterRoot>
      <HeaderBox>
        <Header>{t('recipes-overview:other-products.headline')}</Header>
      </HeaderBox>
      {showFrozen && (
        <RecipesBox>
          <RecipesColumn>
            <BodyBox>
              <Title>{t('recipes-overview:other-products.frozen.title')}</Title>
              <Description>{t('recipes-overview:other-products.frozen.description')}</Description>
            </BodyBox>
            <RecipeIcons>
              {frozenProteins.map((meat, index) => (
                <Icon key={`frozen-${index}`} src={meat.icon} text={meat.alt} link={meat.link} />
              ))}
            </RecipeIcons>
            <Link passHref href="/recipes">
              <SeeMoreButton variant="secondaryAlt">
                {t('recipes-overview:other-products.frozen.cta-button')}
              </SeeMoreButton>
            </Link>
          </RecipesColumn>
          <ImageColumn>
            <ImageWrapper>
              <Image
                layout="fill"
                objectFit="cover"
                src={frozenMeat}
                alt={t('recipes-overview:other-products.frozen.image-alt')}
              />
            </ImageWrapper>
          </ImageColumn>
        </RecipesBox>
      )}
      {showFreezeDried && (
        <RecipesBox>
          <RecipesColumn>
            <BodyBox>
              <Title>{t('recipes-overview:other-products.freeze-dried.title')}</Title>
              <Description>
                {t('recipes-overview:other-products.freeze-dried.description')}
              </Description>
            </BodyBox>
            <RecipeIcons>
              {freezeDriedProteins.map((meat, index) => (
                <Icon
                  key={`freeze-dried-${index}`}
                  src={meat.icon}
                  text={meat.alt}
                  link={meat.link}
                />
              ))}
            </RecipeIcons>
            <Link passHref href="/recipes/freeze-dried-raw">
              <SeeMoreButton variant="secondaryAlt">
                {t('recipes-overview:other-products.freeze-dried.cta-button')}
              </SeeMoreButton>
            </Link>
          </RecipesColumn>
          <ImageColumn className="freeze">
            <ImageWrapper>
              <Image
                layout="fill"
                objectFit="cover"
                src={freezeDried}
                alt={t('recipes-overview:other-products.freeze-dried.image-alt')}
              />
            </ImageWrapper>
          </ImageColumn>
        </RecipesBox>
      )}
    </FeedBetterRoot>
  )
}

FeedBetter.displayName = 'FeedBetter'
